import { EmptyState } from "@/components/molecules/empty-state";
import { HighlightedSearchInput } from "@/components/molecules/highlighted-search-input";
import { SearchFilterHelp } from "@/components/molecules/search-filter-help/index";
import {
  BulkActionsBar,
  type BulkAction,
} from "@/components/molecules/tables/bulk-actions";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AuditableEntity } from "@/features/types";
import { useTranslation } from "@/features/utils/i18n";
import { parseSearchFilters } from "@/features/utils/search-filter-parser";
import { cn } from "@/lib/utils";
import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CaretUpIcon,
  MagnifyingGlassIcon,
  SlidersHorizontalIcon,
  SortAscendingIcon,
  SortDescendingIcon,
} from "@phosphor-icons/react";
import type {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigateAlt } from "@/features/utils/use-navigate";

/** Page sizes offered under a paginated table. */
const PAGE_SIZES = [10, 25, 50];

/**
 * What a click on a row must leave alone: the controls inside it keep their own
 * meaning ("Voir", the "..." menu, a link, the selection checkbox).
 */
const INTERACTIVE =
  'a, button, input, select, textarea, label, [role="checkbox"], [role="button"], [role="menuitem"], [role="combobox"], [data-row-click-ignore]';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data?: TData[];
  isLoading?: boolean;
  isFetching?: boolean;
  total?: number;
  pageCount?: number;
  pagination?: PaginationState;
  setPagination?: OnChangeFn<PaginationState>;
  sorting?: SortingState;
  setSorting?: OnChangeFn<SortingState>;
  globalFilter?: string;
  setGlobalFilter?: (search: string, filters?: Record<string, unknown>) => void;
  columnVisibility?: VisibilityState;
  setColumnVisibility?: OnChangeFn<VisibilityState>;
  onRefresh?: () => void;
  hideSearch?: boolean;
  hidePagination?: boolean;
  searchBar?: (searchBar: React.ReactNode) => React.ReactNode;
  stickyColumnsCount?: number;
  /**
   * Grouped actions applied to the checked rows. Passing a non-empty list adds
   * the selection column and the toolbar; pass an empty list (or omit it) for
   * users who aren't allowed to run them and the table renders as before.
   */
  bulkActions?: BulkAction<TData>[];
  /** Rows that cannot be part of a selection (e.g. yourself in a member list). */
  canSelectRow?: (row: TData) => boolean;
  /** Identity of a row, used to keep the selection stable across re-renders. */
  getRowId?: (row: TData, index: number) => string;
  /**
   * Adds the "Colonnes" dropdown to the toolbar. Off by default: most tables
   * here have hand-picked columns, and only the generic back-office grid — whose
   * columns come from a database schema — has enough of them to need hiding.
   */
  showColumnSettings?: boolean;
  /** Extra toolbar content, rendered to the right of the search box. */
  toolbar?: React.ReactNode;
  /**
   * Where a row leads. Returning a path makes the whole row clickable (and
   * middle-click / cmd-click open it in a new tab, via `useNavigateAlt`).
   */
  onRowNavigate?: (row: TData) => string | null;
  /**
   * What a click on a row does when it has no address of its own (a details
   * dialog). `onRowNavigate` wins for a row it returns a path for.
   */
  onRowClick?: (row: TData) => void;
  /**
   * Rows that are behind us — an appointment that took place, a follow-up that
   * went out, a request that was handled — drawn greyed out, so what is still
   * to come or still to do stands out in a list that mixes both. Hovering a row
   * gives it back its full contrast.
   */
  isRowMuted?: (row: TData) => boolean;
  /** Replaces the default "no results" line when the table is empty. */
  emptyState?: React.ReactNode;
  /** Row height: `compact` for long lists and tables inside a card. */
  density?: "comfortable" | "compact";
  /**
   * `embedded` is a table inside a card: no search bar, no page-size picker,
   * and the pager only shows up when there is a second page. Compact by default.
   */
  variant?: "default" | "embedded";
}

export function DataTable<TData, TValue>({
  columns,
  data = [],
  isLoading = false,
  isFetching = false,
  total = 0,
  hidePagination = false,
  pageCount = 0,
  pagination = { pageIndex: 0, pageSize: 25 },
  setPagination,
  sorting = [],
  setSorting,
  globalFilter = "",
  setGlobalFilter,
  columnVisibility = {},
  setColumnVisibility,
  hideSearch = false,
  stickyColumnsCount = 1,
  searchBar,
  bulkActions,
  canSelectRow,
  getRowId,
  showColumnSettings = false,
  toolbar,
  onRowNavigate,
  onRowClick,
  isRowMuted,
  emptyState,
  density,
  variant = "default",
}: DataTableProps<TData, TValue>) {
  const embedded = variant === "embedded";
  const compact = (density ?? (embedded ? "compact" : "comfortable")) === "compact";
  const { t } = useTranslation("table");
  const [searchParams] = useSearchParams();
  const navigate = useNavigateAlt();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [searchInput, setSearchInput] = useState(globalFilter);
  const [isSearching, setIsSearching] = useState(false);
  const hasLoadedQueryParam = useRef(false);

  // Determine if we're using backend or client-side operations
  const hasBackendSorting = !!setSorting;
  const hasBackendFiltering = !!setGlobalFilter;
  const hasBackendPagination = !!setPagination;

  // Client-side state for when no backend handlers provided
  const [clientSorting, setClientSorting] = useState<SortingState>(sorting);
  const [clientGlobalFilter, setClientGlobalFilter] = useState(globalFilter);
  const [clientPagination, setClientPagination] =
    useState<PaginationState>(pagination);

  // Initialize search from query string parameter 'q' if present - only on mount
  useEffect(() => {
    if (hasLoadedQueryParam.current) return;

    const qParam = searchParams.get("q");
    if (qParam) {
      hasLoadedQueryParam.current = true;
      setSearchInput(qParam);
      if (hasBackendFiltering) {
        setGlobalFilter?.(qParam);
      } else {
        setClientGlobalFilter(qParam);
      }
    }
  }, []); // Only run once on mount

  useEffect(() => {
    setSearchInput(globalFilter);
  }, [globalFilter]);

  // Debounce search input
  useEffect(() => {
    // Don't trigger search if the input matches current filter
    if (searchInput === globalFilter || searchInput === clientGlobalFilter) {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    const timer = setTimeout(
      () => {
        if (hasBackendFiltering) {
          // Parse search filters before calling setGlobalFilter
          const parsed = parseSearchFilters(searchInput);
          setGlobalFilter?.(parsed.search, parsed.filters);
        } else {
          setClientGlobalFilter(searchInput);
        }
        setIsSearching(false);
      },
      hasBackendFiltering ? 1000 : 200,
    );

    return () => {
      clearTimeout(timer);
      setIsSearching(false); // Always reset when cleaning up
    };
  }, [searchInput, hasBackendFiltering, setGlobalFilter]);

  const selectionEnabled = !!bulkActions?.length;

  // Falls back to the entity id so the selection survives a refetch; index-based
  // ids would silently re-map the checked rows onto their new neighbours.
  const resolveRowId = useMemo(
    () =>
      getRowId ??
      ((row: TData, index: number) =>
        String((row as { id?: string | number })?.id ?? index)),
    [getRowId],
  );

  // Rows that disappeared (deleted, filtered out, next page) must not stay in
  // the selection — otherwise the toolbar counts rows nobody can see.
  useEffect(() => {
    if (!selectionEnabled) return;
    setRowSelection((previous) => {
      const keys = Object.keys(previous);
      if (keys.length === 0) return previous;
      const visible = new Set(
        data.map((row, index) => resolveRowId(row, index)),
      );
      const kept = keys.filter((key) => visible.has(key));
      if (kept.length === keys.length) return previous;
      return Object.fromEntries(kept.map((key) => [key, true]));
    });
  }, [data, selectionEnabled, resolveRowId]);

  const tableColumns = useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!selectionEnabled) return columns;

    const selectionColumn: ColumnDef<TData, TValue> = {
      id: "__select",
      enableSorting: false,
      enableHiding: false,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t("table.selectAll")}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          onClick={(event) => event.stopPropagation()}
          aria-label={t("table.selectRow")}
        />
      ),
    };

    return [selectionColumn, ...columns];
  }, [columns, selectionEnabled, t]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    // Only when selecting: elsewhere the default index-based row ids are fine
    // and a table whose rows have no unique `id` keeps working as before.
    getRowId: selectionEnabled ? resolveRowId : undefined,
    enableRowSelection: selectionEnabled
      ? canSelectRow
        ? (row) => canSelectRow(row.original)
        : true
      : false,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: hasBackendSorting ? setSorting : setClientSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: hasBackendPagination
      ? setPagination
      : setClientPagination,
    onGlobalFilterChange: hasBackendFiltering
      ? setGlobalFilter
      : setClientGlobalFilter,
    state: {
      sorting: hasBackendSorting ? sorting : clientSorting,
      columnFilters,
      columnVisibility,
      pagination: hasBackendPagination ? pagination : clientPagination,
      globalFilter: hasBackendFiltering ? globalFilter : clientGlobalFilter,
      rowSelection,
    },
    manualPagination: hasBackendPagination,
    manualSorting: hasBackendSorting,
    manualFiltering: hasBackendFiltering,
    pageCount: hasBackendPagination ? pageCount : undefined,
  });

  const currentPagination = hasBackendPagination
    ? pagination
    : clientPagination;

  // The sticky columns stack from the right edge, so each one sits at the summed
  // width of those after it. Measured, not assumed: a fixed 120px offset left a
  // gap behind a narrow action column and overlapped a wide one.
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const [stickyWidths, setStickyWidths] = useState<number[]>([]);
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  useLayoutEffect(() => {
    const row = headerRowRef.current;
    // The last column sits at right: 0 whatever its width.
    if (!row || stickyColumnsCount < 2) return;
    const cells = Array.from(row.cells).slice(-stickyColumnsCount).reverse();
    const measure = () => {
      const widths = cells.map((cell) => cell.getBoundingClientRect().width);
      setStickyWidths((previous) =>
        previous.length === widths.length &&
        previous.every((width, index) => width === widths[index])
          ? previous
          : widths,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    cells.forEach((cell) => observer.observe(cell));
    return () => observer.disconnect();
  }, [stickyColumnsCount, visibleColumnCount]);

  const sticky = (index: number, total: number, cell: string) => {
    if (stickyColumnsCount <= 0 || index < total - stickyColumnsCount) {
      return {};
    }
    const stickyIndex = total - index - 1;
    const isEdge = stickyIndex === stickyColumnsCount - 1;
    return {
      className: cn(
        "sticky z-10",
        cell,
        isEdge &&
          "border-l shadow-[-6px_0_8px_-6px_rgb(var(--shadow-color)/0.12)]",
      ),
      style: {
        right: stickyWidths
          .slice(0, stickyIndex)
          .reduce((sum, width) => sum + width, 0),
      },
    };
  };
  // A sticky cell must be opaque, or the columns scrolling under it show
  // through; it paints the row's tint (hover, selection, deleted) over the card
  // itself, since the row's own background is behind it.
  const stickyBodyCell =
    "bg-card bg-[image:linear-gradient(var(--row-tint),var(--row-tint))] transition-colors group-hover/row:bg-muted";

  const rowIsClickable = !!(onRowNavigate || onRowClick);
  /**
   * Whether a click belongs to the row rather than to something inside it. React
   * bubbles events out of portals (a row's menu, its dialog) as if they happened
   * inside the row, so only a click that really landed in it counts; and a
   * control inside the row keeps its own meaning — a middle-click on a link opens
   * that link, not the row as well.
   */
  const isRowOwnClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement;
    if (!event.currentTarget.contains(target)) return false;
    const control = target.closest(INTERACTIVE);
    return !(control && event.currentTarget.contains(control));
  };

  const activateRow = (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    if (!isRowOwnClick(event)) return;
    // Selecting a phone number to copy it is not asking to open the row, and a
    // double-click must not open it twice.
    if (window.getSelection()?.toString() || event.detail > 1) return;
    const path = onRowNavigate?.(row) ?? null;
    if (path) navigate(path, event);
    else onRowClick?.(row);
  };

  const cellPadding = compact ? "px-3 py-1.5" : "px-4 py-2";
  const headPadding = compact ? "h-9 px-3" : "px-4";
  const showSearch = !hideSearch && !embedded;
  const showToolbar = showSearch || !!toolbar || showColumnSettings;

  return (
    <div className={embedded ? "space-y-3" : "space-y-4"}>
      {/* Toolbar */}
      {showToolbar && (
        <div className="flex flex-wrap items-center gap-2">
          {showSearch && (
            <div className="flex min-w-0 flex-[1_1_16rem] flex-wrap items-center gap-2">
              {(() => {
                // Default search bar component
                const defaultSearchBar = (
                  <>
                    {/* Global Search */}
                    <div className="relative min-w-48 flex-1">
                      {!isSearching && !isFetching && (
                        <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground z-20" />
                      )}
                      {(isSearching || isFetching) && (
                        <Spinner className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground z-20" />
                      )}
                      <div className="relative border border-input rounded-md bg-card shadow-xs pl-6 h-9">
                        <HighlightedSearchInput
                          placeholder={t("table.search")}
                          value={searchInput}
                          onChange={(value) => setSearchInput(value)}
                          className="w-full h-9 border-0 bg-transparent"
                        />
                      </div>
                    </div>

                    {/* The key=value syntax is only parsed on a server-side
                        search; a local one matches the text as typed. */}
                    {hasBackendFiltering && <SearchFilterHelp />}
                  </>
                );

                // Use custom searchBar render function if provided, otherwise use default
                return searchBar
                  ? searchBar(defaultSearchBar)
                  : defaultSearchBar;
              })()}
            </div>
          )}

          {(toolbar || showColumnSettings) && (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {toolbar}
              {showColumnSettings && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9">
                      <SlidersHorizontalIcon className="h-4 w-4" />
                      {t("table.columns")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-[70vh] w-56 overflow-y-auto"
                  >
                    {table
                      .getAllColumns()
                      .filter((column) => column.getCanHide())
                      .map((column) => (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                          onSelect={(event) => event.preventDefault()}
                        >
                          {String(column.columnDef?.header ?? column.id)}
                        </DropdownMenuCheckboxItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>
      )}

      {selectionEnabled && (
        <BulkActionsBar
          selectedRows={table
            .getSelectedRowModel()
            .rows.map((row) => row.original)}
          actions={bulkActions ?? []}
          onClear={() => setRowSelection({})}
        />
      )}

      <div className="rounded-lg border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} ref={headerRowRef}>
                {headerGroup.headers.map((header, headerIndex) => {
                  const stickyProps = sticky(
                    headerIndex,
                    headerGroup.headers.length,
                    "bg-muted",
                  );
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(headPadding, stickyProps.className)}
                      style={stickyProps.style}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={
                            header.column.getCanSort()
                              ? "flex items-center gap-2 cursor-pointer select-none hover:bg-foreground/5 hover:text-foreground -mx-2 px-2 py-1 rounded-sm w-max"
                              : "flex items-center gap-2 w-max"
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span className="whitespace-nowrap">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </span>
                          {header.column.getCanSort() && (
                            <div className="flex flex-col">
                              {header.column.getIsSorted() === "desc" ? (
                                <SortDescendingIcon className="h-3 w-3" />
                              ) : header.column.getIsSorted() === "asc" ? (
                                <SortAscendingIcon className="h-3 w-3" />
                              ) : (
                                <div className="flex flex-col">
                                  <CaretUpIcon className="h-2 w-2 -mb-1" />
                                  <CaretDownIcon className="h-2 w-2" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading skeleton: a page's worth, but never a wall of 50 rows
              Array.from({
                length: Math.min(currentPagination.pageSize || 10, 10),
              }).map((_, index) => (
                <TableRow key={index} className="hover:bg-transparent">
                  {table.getVisibleLeafColumns().map((column, colIndex) => {
                    const stickyProps = sticky(
                      colIndex,
                      visibleColumnCount,
                      "bg-card",
                    );
                    return (
                      <TableCell
                        key={column.id}
                        className={cn(cellPadding, stickyProps.className)}
                        style={stickyProps.style}
                      >
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const isDeleted = !!(row.original as AuditableEntity)
                  .is_deleted;
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={cn(
                      "[--row-tint:transparent] data-[state=selected]:[--row-tint:color-mix(in_srgb,var(--info-soft)_60%,transparent)]",
                      rowIsClickable && "cursor-pointer",
                      isRowMuted?.(row.original) &&
                        "text-muted-foreground opacity-60 transition-opacity hover:opacity-100",
                      isDeleted &&
                        "bg-danger-soft [--row-tint:var(--danger-soft)] opacity-50 line-through",
                    )}
                    onClick={
                      rowIsClickable
                        ? (event) => activateRow(row.original, event)
                        : undefined
                    }
                    onAuxClick={
                      onRowNavigate
                        ? (event) => {
                            if (event.button !== 1 || !isRowOwnClick(event))
                              return;
                            const path = onRowNavigate(row.original);
                            if (path) navigate(path, event);
                          }
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell, cellIndex, cells) => {
                      const stickyProps = sticky(
                        cellIndex,
                        cells.length,
                        stickyBodyCell,
                      );
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(cellPadding, stickyProps.className)}
                          style={stickyProps.style}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={visibleColumnCount}
                  className="p-0 text-center"
                >
                  {emptyState ?? (
                    <EmptyState
                      compact
                      icon={MagnifyingGlassIcon}
                      title={t("table.noResults")}
                    />
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!hidePagination &&
        (() => {
          const currentTotal = hasBackendPagination
            ? total
            : table.getFilteredRowModel().rows.length;
          const currentPageCount = hasBackendPagination
            ? pageCount
            : table.getPageCount();
          const { pageIndex, pageSize } = currentPagination ?? {};

          if (!pageSize || currentTotal === 0) return null;
          // Inside a card, a pager for a single page is only noise.
          if (embedded && currentPageCount <= 1) return null;

          // The picker only offers what changes something: a size larger than
          // the whole list is the same page as the one before it.
          const pageSizes = [...new Set([...PAGE_SIZES, pageSize])]
            .sort((a, b) => a - b)
            .filter(
              (_size, index, sizes) =>
                index === 0 || sizes[index - 1] < currentTotal,
            );
          const showPageSizes = !embedded && pageSizes.length > 1;

          return (
            <div
              className={cn(
                "flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground",
                showPageSizes ? "justify-between" : "justify-end",
              )}
            >
              {showPageSizes && (
                <div className="flex items-center gap-2">
                  <span>{t("table.rowsPerPage")}</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(value) =>
                      table.setPagination({
                        pageIndex: 0,
                        pageSize: Number(value),
                      })
                    }
                  >
                    <SelectTrigger
                      className="h-8 w-auto gap-1 text-foreground"
                      aria-label={t("table.rowsPerPage")}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent align="start">
                      {pageSizes.map((size) => (
                        <SelectItem key={size} value={String(size)}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  aria-label={t("table.previous")}
                >
                  <CaretLeftIcon className="h-4 w-4" />
                </Button>
                <span className="px-1 tabular-nums">
                  {t("table.range", {
                    from: pageIndex * pageSize + 1,
                    to: Math.min((pageIndex + 1) * pageSize, currentTotal),
                    total: currentTotal,
                  })}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  aria-label={t("table.next")}
                >
                  <CaretRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
